import React, { useState } from "react";
import { ArrowRight, ExternalLink, ShieldCheck, Sparkles, Globe2 } from "lucide-react";
import { lmsApi } from "../api";
import { useAuth } from "../hooks/useAuth";

const TraineeLmsPortal: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "SYSTEM_ADMIN" || user?.role === "OPERATIONS_ADMIN";
  const isTrainer = user?.role === "TRAINER";
  const identityLabel = isAdmin ? "admin ID" : isTrainer ? "trainer ID" : "trainee ID";

  const handleLaunch = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await lmsApi.getTraineeLoginUrl();
      const redirect = resp.data?.redirectUrl || resp.data?.redirect_url;
      if (!redirect) {
        throw new Error("Unable to fetch login link. Please try again.");
      }
      window.location.href = redirect;
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Could not launch LMS. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary-600 via-primary-500 to-indigo-500 text-white shadow-lg">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.2),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.15),transparent_30%)]" />
        <div className="relative p-8 sm:p-10 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-white/15 rounded-full p-2">
              <Globe2 className="h-5 w-5" />
            </div>
            <p className="uppercase tracking-[0.15em] text-xs font-semibold text-white/80">Knowlens LMS</p>
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold leading-tight">Jump into Knowlens LMS in one click</h1>
          <p className="text-white/90 text-sm sm:text-base max-w-2xl">
            We'll generate a secure, single-sign-on link and take you directly into the Knowlens LMS.
          </p>
          <div className="flex flex-wrap gap-4 text-white/90 text-sm">
            <span className="inline-flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
              <ShieldCheck className="h-4 w-4" /> Signed securely with HMAC
            </span>
            <span className="inline-flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
              <Sparkles className="h-4 w-4" /> No extra login required
            </span>
          </div>
          <div className="pt-2">
            <button
              type="button"
              onClick={handleLaunch}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg bg-white text-primary-700 px-5 py-2.5 text-sm font-semibold shadow-md hover:shadow-lg hover:-translate-y-px transition disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
                  Connecting...
                </span>
              ) : (
                <>
                  Open LMS Portal
                  <ExternalLink className="h-4 w-4" />
                </>
              )}
            </button>
            {error && (
              <p className="mt-2 text-sm text-rose-50/90 bg-white/10 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg">
                <ArrowRight className="h-4 w-4" /> {error}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">What happens next?</h3>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex gap-2">
              <ArrowRight className="h-4 w-4 text-primary-600 mt-0.5" />
              We create a signed link using your {identityLabel} and open Knowlens LMS.
            </li>
            <li className="flex gap-2">
              <ArrowRight className="h-4 w-4 text-primary-600 mt-0.5" />
              You stay logged in here; your LMS session is independent.
            </li>
            <li className="flex gap-2">
              <ArrowRight className="h-4 w-4 text-primary-600 mt-0.5" />
              If you hit an error, try again or contact support with a screenshot.
            </li>
          </ul>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Tips for a smooth launch</h3>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex gap-2">
              <ArrowRight className="h-4 w-4 text-primary-600 mt-0.5" />
              Keep this tab open if you need to come back to the portal.
            </li>
            <li className="flex gap-2">
              <ArrowRight className="h-4 w-4 text-primary-600 mt-0.5" />
              Ensure your browser allows popups for this site.
            </li>
            <li className="flex gap-2">
              <ArrowRight className="h-4 w-4 text-primary-600 mt-0.5" />
              Use a stable internet connection while in the LMS.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TraineeLmsPortal;
